import { useCallback, ReactNode } from 'react';
import { Drawer } from '@glu/drawer-react';
import { Tabs, Tab } from '@glu/tabs-react';
import { Button, TERTIARY } from '@glu/buttons-react';
import { XCircleAshIcon } from '@glu/icons-react';
import locale from '@glu/locale';
import { DarkMode } from '@glu/theming';
import classNames from 'classnames';
import useStyles from './ColumnFilterDrawer.styles';

type TActiveTab = 'columns' | 'filters';

export interface IColumnFilterDrawerProps {
  /** Whether or not the drawer is open */
  open: boolean;
  /** Children to render inside the drawer */
  children: ReactNode;
  /** Callback used to close the drawer */
  onClose(): void;
  /** Will hide filters if true */
  hideFilters?: boolean;
  /** Will hide columns if true */
  hideColumns?: boolean;
  /** Defines which tab of the drawer is currently active */
  activeTab?: TActiveTab;
  /** Callback to change the active tab on the drawer */
  onChangeActiveTab(newTab: TActiveTab): void;
  /** CSS classes applied to the root element of this component. */
  className?: string;
  /** Html id attribute applied to the root element of this component. */
  htmlId?: string;
}

const ColumnFilterDrawer = ({
  activeTab,
  children,
  className,
  hideColumns,
  hideFilters,
  htmlId,
  onChangeActiveTab,
  onClose,
  open,
}: IColumnFilterDrawerProps) => {
  const classes = useStyles();
  const handleTabChange = useCallback(
    (_: any, value: TActiveTab) => {
      onChangeActiveTab(value);
    },
    [onChangeActiveTab]
  );
  return (
    <DarkMode>
      <Drawer anchorTo="right" className={classes.drawer} open={open} size={450} startingSize={0}>
        <div className={classNames(classes.background, className)} id={htmlId}>
          <Button
            aria-label={locale.get('dataComponents.close')}
            className={classes.closeButton}
            data-qa="drawerClose"
            onClick={onClose}
            variant={TERTIARY}
          >
            <XCircleAshIcon />
          </Button>
          <div className={classes.headerWrapper}>
            <Tabs value={activeTab}>
              {!hideFilters && (
                <Tab className={classes.tab} onChange={handleTabChange} value="filters">
                  {locale.get('dataComponents.filters')}
                </Tab>
              )}
              {!hideColumns && (
                <Tab className={classes.tab} onChange={handleTabChange} value="columns">
                  {locale.get('dataComponents.columns')}
                </Tab>
              )}
            </Tabs>
          </div>
          {children}
        </div>
      </Drawer>
    </DarkMode>
  );
};

export default ColumnFilterDrawer;
